/*
 * Copyright (c) 2023 GM Global Technology Operations LLC. All rights reserved.
 * This information is confidential and proprietary to GM Global Technology
 * Operations LLC and may not be used, modified, copied or distributed.
 */
import { HttpClient, HttpErrorResponse, HttpParams } from '@angular/common/http';
import {Injectable} from '@angular/core';
import {Observable, throwError} from 'rxjs';
import {catchError} from 'rxjs/operators';
import {StateService} from '../core/state/state.service';
import {MarketingDivisionModel} from '../models/marketing-division.model';
import {ConfigService} from './config.service';
import {RemoveObsoleteRecordsService} from './remove-obsolete-records.service';

@Injectable({
  providedIn: 'root'
})
export class MarketingDivisionService implements RemoveObsoleteRecordsService {

  constructor(private readonly configService: ConfigService,
              private readonly http: HttpClient,
              private readonly user: StateService) {
  }

  /**
   * Sends a get request to retrieve list of all market divisions in the database.
   */
  getCurrentMarketDivisions(): Observable<any> {
    return this.http.get<string[]>(`${this.configService.baseApiUrl}market-division/string`)
    .pipe(catchError(myError => this.handleError(myError)));
  }


  /**
   * Sends a get request to retrieve list of all market divisions in the database.
   */
  getAllMarketDivisions(): Observable<any> {
    return this.http.get<any[]>(`${this.configService.baseApiUrl}market-division`)
    .pipe(catchError(myError => this.handleError(myError)));
  }

  /**
   * gets all market divisions associated with a given propulsion scenario
   * @param propScenId id of a given propulsion scenario
   */
  getMarketDivisionsByPropulsionScenario(propScenId: number): Observable<string[]> {
    const params = new HttpParams()
    .set('prop-scen-id', propScenId);
    return this.http.get<string[]>(`${this.configService.baseApiUrl}market-division`, {params})
    .pipe(catchError(myError => this.handleError(myError)));
  }

  /**
   * Sends a post request to add a new market division to the database.
   * @param marketDivision user inputted market division to add
   */
  addMarketDivision(marketDivision: string): Observable<any> {
    const mktDiv = new MarketingDivisionModel();
    mktDiv.marketDivision = marketDivision;
    mktDiv.userNetId = this.user.getUserGmid();
    return this.http.post(`${this.configService.baseApiUrl}market-division`, mktDiv)
    .pipe(catchError(myError => this.handleError(myError)));
  }

  /**
   * Sends a get request to retrieve list of all obsolete market divisions in the database.
   */
  getObsoleteRecords(): Observable<any> {
    return this.http.get<string[]>(`${this.configService.baseApiUrl}manage-gfs-vehicles/obsolete-market-divisions`)
    .pipe(catchError(myError => this.handleError(myError)));
  }

  /**
   * Sends a delete request to delete an obsolete market division in the database.
   */
  removeObsoleteRecords(): Observable<any> {
    return this.http.delete(`${this.configService.baseApiUrl}manage-gfs-vehicles/obsolete-market-divisions`)
    .pipe(catchError(myError => this.handleError(myError)));
  }

  /**
   * Handles the errors produced by the backend
   * @param errorResponse The error produced by the API to be handled by the frontend
   * @return void
   */
  handleError(errorResponse: HttpErrorResponse): Observable<any> {
    let errorMessage = 'An unexpected error has occurred.';
    if (errorResponse.error && errorResponse.error.message) {
      errorMessage = errorResponse.error.message;
    }
    return throwError(errorMessage);
  }
}
